import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { cn } from '@/lib/utils'
import { useFluidAnimation } from '@/hooks/use-fluid-animation'

/**
 * FluidHoverIndicator Props
 */
export interface FluidHoverIndicatorProps {
  /** Target element to track */
  targetElement: HTMLElement | null
  /** Whether the indicator should be visible */
  isVisible: boolean
  /** Optional className for customization */
  className?: string
  /** Container ref for relative positioning (default: targetElement.parentElement) */
  containerRef?: RefObject<HTMLElement>
}

/**
 * Fluid Hover Indicator Component
 *
 * A liquid drop-like hover indicator that uses spring physics
 * and morphing to create organic, fluid motion between sidebar items.
 *
 * Features:
 * - Spring physics with anticipation, stretch, overshoot, and settle
 * - Morphing (scale, skew) based on movement direction
 * - Pure CSS + Web Animations API (no Framer Motion)
 * - GPU-accelerated with will-change and transform
 */
export function FluidHoverIndicator({
  targetElement,
  isVisible,
  className = '',
  containerRef,
}: FluidHoverIndicatorProps) {
  const indicatorRef = useRef<HTMLDivElement>(null)
  const { animate, reset } = useFluidAnimation()
  const isFirstRender = useRef(true)

  useEffect(() => {
    console.log('🎨 [FluidHoverIndicator] useEffect:', {
      hasTargetElement: !!targetElement,
      hasIndicatorRef: !!indicatorRef.current,
      isVisible,
      targetElementRect: targetElement?.getBoundingClientRect(),
    })

    if (!targetElement || !indicatorRef.current || !isVisible) {
      // Reset animation state when hiding
      if (!isVisible && indicatorRef.current) {
        reset()
        indicatorRef.current.style.opacity = '0'
        isFirstRender.current = true
      }
      return
    }

    // Get target position
    const targetRect = targetElement.getBoundingClientRect()

    // Use containerRef if provided, otherwise fallback to parentElement
    const parentRect = containerRef?.current?.getBoundingClientRect()
      || targetElement.parentElement?.getBoundingClientRect()

    if (!parentRect) return

    // Calculate position relative to sidebar
    // Reduce dimensions to leave space for SidebarIndicator
    const padding = 8 // Space for the sidebar indicator bar
    const position = {
      x: targetRect.left - parentRect.left + padding,
      y: targetRect.top - parentRect.top + 4,
      width: targetRect.width - padding - 4,
      height: targetRect.height - 8,
    }

    // First render: instant positioning without animation
    if (isFirstRender.current) {
      indicatorRef.current.style.transform = `translate(${position.x}px, ${position.y}px)`
      indicatorRef.current.style.width = `${position.width}px`
      indicatorRef.current.style.height = `${position.height}px`
      indicatorRef.current.style.opacity = '1'
      isFirstRender.current = false
      return
    }

    // Animate to new position with fluid physics
    animate(indicatorRef.current, position)
  }, [targetElement, isVisible, animate, reset, containerRef])

  return (
    <div
      ref={indicatorRef}
      className={cn(
        // Base styling
        'pointer-events-none absolute z-20',
        'rounded-xl',
        // Glassmorphism effect - AUMENTADO para mejor visibilidad
        'bg-primary/30 backdrop-blur-md',
        // Border with glow - AUMENTADO para mejor visibilidad
        'border-2 border-primary/80',
        'shadow-lg shadow-primary/40',
        // GPU acceleration
        'will-change-transform',
        // Visibility
        isVisible ? 'opacity-100' : 'opacity-0',
        // Transition for opacity only (position handled by animate())
        'transition-opacity duration-200',
        // Custom classes
        className
      )}
      style={{
        // Performance optimization
        contain: 'layout style paint',
        // Initial state
        transform: 'translate(0, 0) scale(1) skew(0deg)',
        // Force hide when not visible
        opacity: isVisible ? undefined : 0,
      }}
    >
      {/* Optional: Inner glow effect - AUMENTADO para mejor visibilidad */}
      <div
        className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/40 to-transparent opacity-80"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  )
}
